import React, { useState } from "react";
import { ActionIcon, Anchor, FileInput, Group, Input, Loader, Tooltip } from "@mantine/core";
import { IconPaperclip, IconUpload, IconX } from "@tabler/icons-react";
import { bidCommitteeApi, type AgendaTenderDocument } from "../../../api/bidCommittee";
import { useDownloadAgendaTenderDocument } from "../hooks";

export type AgendaTenderDocumentValue = Pick<AgendaTenderDocument, "id" | "fileName">;

interface AgendaTenderDocumentFieldProps {
  value: AgendaTenderDocumentValue | null;
  onChange: (value: AgendaTenderDocumentValue | null) => void;
  /** Lets the parent form block saving while a file is still uploading */
  onUploadingChange?: (uploading: boolean) => void;
}

const LABEL = "Tender Document (Optional)";
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png";

/** Uploads the file as soon as it is picked; the agenda item keeps only the returned document id. */
export const AgendaTenderDocumentField: React.FC<AgendaTenderDocumentFieldProps> = ({
  value,
  onChange,
  onUploadingChange,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const download = useDownloadAgendaTenderDocument();

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const { data } = await bidCommitteeApi.uploadAgendaTenderDocument(file);
      onChange({ id: data.id, fileName: data.fileName });
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to upload document");
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  if (value) {
    return (
      <Input.Wrapper label={LABEL}>
        <Group gap="xs" wrap="nowrap" mt={4}>
          <IconPaperclip size={16} />
          <Anchor
            component="button"
            type="button"
            size="sm"
            style={{ wordBreak: "break-all", textAlign: "left" }}
            onClick={() => download.mutate({ documentId: value.id, fileName: value.fileName })}
          >
            {value.fileName}
          </Anchor>
          <Tooltip label="Remove document">
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              aria-label="Remove document"
              onClick={() => onChange(null)}
            >
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Input.Wrapper>
    );
  }

  return (
    <FileInput
      label={LABEL}
      description="PDF, Word, Excel, or image (JPG/PNG), up to 50 MB"
      placeholder={uploading ? "Uploading…" : "Select a file to upload"}
      accept={ACCEPT}
      value={null}
      onChange={handleFile}
      disabled={uploading}
      leftSection={uploading ? <Loader size={14} /> : <IconUpload size={16} />}
      error={error}
    />
  );
};
